import type {
    Mode,
    Currency,
    Theme,
    Layout,
    Language,
    ApplePayPreset,
    GooglePayPreset,
    Wallet,
    IntegrationProfileId,
    CreateFormUrlMethod,
    TokenizeFlow,
    LinkTarget,
} from '@payments-frontend/shared/contracts';

import { Poller } from '../lib/poller';
import { FrameApplePayOutgoingMessage } from '../methods/apple-pay/frame-apple-pay-message';

import { ApiHost } from './api';

/**
 * Payment methods types
 */
export enum PaymentMethodType {
    card = 'card',
    sbpToken = 'sbp_token',
}

export type BankCardId = string;

export type BankCardNumber = string;

export type BankCardIssuer = string;

export enum BankCardSystem {
    visa = 'visa',
    mastercard = 'mastercard',
    maestro = 'maestro',
    mir = 'mir',
    unknown = 'unknown',
}

interface PartnerInfo {
    isYabankCard?: boolean;
    isYabankCardOwner?: boolean;
    yabankCardLabels?: string[];
}

export interface BankCard {
    id: BankCardId;
    type: PaymentMethodType.card;
    number: BankCardNumber;
    system: BankCardSystem;
    partnerInfo?: PartnerInfo;
    issuer?: BankCardIssuer;
}

export interface ApiSbpIconUrls {
    /**
     * Ссылка на иконку для темной темы
     * @example "https://avatars.mdst.yandex.net/get-fintech/1401668/pir_bank.png"
     */
    dark?: string;
    /**
     * Ссылка на иконку для светлой темы
     * @example "https://avatars.mdst.yandex.net/get-fintech/1401668/pir_bank.png"
     */
    light?: string;
}

export interface SbpToken {
    /**
     * Идентификатор токена (без префикса sbp-)
     * @example 523947a609ba319635697f6b
     */
    id: string;

    /**
     * Название токена (по умолчанию sbp-<id>)
     * @example "sbp-523947a609ba319635697f6b"
     */
    name: string;
    method_id: string;

    icon_url?: ApiSbpIconUrls;

    /**
     * Таймстемп привязки
     * @example 1681213666
     *
     */
    binding_ts: number;

    /**
     * Идентификатор банка в системе НСПК
     * @example 1crt88888881
     */
    member_id: string;

    /**
     * Названия банка на английском (?) языке
     * @example "PIR Bank"
     */
    member_name: string;

    /**
     * Названия банка на русском языке
     * @example "ПИР Банк"
     */
    member_name_rus: string;

    /**
     * Платежная система
     */
    payment_system: null;

    /**
     * Тип платежного элемента
     */
    type: PaymentMethodType.sbpToken;
}

/**
 * Payment types
 */
export type PaymentFormUrl = string;

export interface PartnerBoundInfo {
    is_yabank_card?: boolean;
    is_yabank_card_owner?: boolean;
    yabank_card_labels?: string[];
}

export interface ApiBankCard {
    id: string;
    method_id: string;
    system: string;
    number: string;
    last_paid_ts: number;
    partner_info?: PartnerBoundInfo;
    card_bank?: string;
}

export interface ApiSbpToken {
    /**
     * Идентификатор токена (без префикса sbp-)
     * @example 523947a609ba319635697f6b
     */
    id: string;

    /**
     * Название токена (по умолчанию sbp-<id>)
     * @example "sbp-523947a609ba319635697f6b"
     */
    name: string;
    method_id: string;

    icon_url?: ApiSbpIconUrls;

    /**
     * Таймстемп привязки
     * @example 1681213666
     *
     */
    binding_ts: number;

    /**
     * Идентификатор банка в системе НСПК
     * @example 1crt88888881
     */
    member_id: string;

    /**
     * Названия банка на английском (?) языке
     * @example "PIR Bank"
     */
    member_name: string;

    /**
     * Названия банка на русском языке
     * @example "ПИР Банк"
     */
    member_name_rus: string;

    /**
     * Платежная система
     */
    payment_system: null;

    /**
     * Тип платежного элемента
     */
    type: PaymentMethodType.sbpToken;
}

export interface BindSbpToken {
    form_url: string;
    payment_method: string;
    purchase_token: string;
    status: string;
    status_desc: string;
    trust_payment_id: string;
    qrc_id?: string;
}

export interface BindSbpTokenParams {
    purchaseToken: string;
}

export interface BindSbpTokenOptions {
    purchaseToken: string;
}

export interface LoadCardsOptions {
    serviceToken?: string;
    /**
     * Возвращать ли привязанные СБП токены
     */
    loadSbpTokens?: boolean;
}

export type ApiPaymentMethod = ApiSbpToken | ApiBankCard;

export interface PaymentMethods {
    // eslint-disable-next-line camelcase
    payment_methods: {
        [key: string]: ApiPaymentMethod;
    };
}

export interface ApplePaySessionParams {
    validationURL: string;
    purchaseToken: string;
    merchantId: string;
}

export interface CreateBinding {
    // eslint-disable-next-line camelcase
    binding_url: string;
}

export interface CreateFormUrl {
    form_url: string;
}

export interface CreateBindingParams {
    mode: string;
    modeSuccess: string;
    isMobile: boolean;
    lang?: string;
    theme?: 'dark' | 'default' | 'auto';
    serviceToken?: string;
    template?: string;
    diehardTemplateTag?: string;
    remoteControl?: boolean;
    currency?: string;
}

export interface BindOptionsBaseV2 {
    /**
     * `default` - показывает `success` и `error` отдельными экранами;
     * `spin` - показывает спинер (лоадер) вместо экранов `success` и `error`;
     * `spin-success` - показывает спинер (лоадер) вместо экрана `success`;
     *
     * @defaultValue 'default'
     */
    mode: `${Mode}`;
    lang: `${Language}`;
    theme?: `${Theme}`;
    serviceToken: string;
    layout?: `${Layout}`;

    /**
     * Integration profile id: yandex_default, yandex_kz, yandex_uz, yango_com, ... etc ...
     *
     * @see https://st.yandex-team.ru/DHKB-6
     */
    integrationProfileId: IntegrationProfileId;
}

export interface CreateFormUrlOptions {
    operationId: string;
    anonymously: boolean;
    integrationProfileId: IntegrationProfileId;
    lang: `${Language}`;
    serviceToken: string;
    layout?: `${Layout}`;
    theme?: `${Theme}`;
    method: CreateFormUrlMethod;
    flow: TokenizeFlow;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type TokenizeCardV2Options = Omit<CreateFormUrlOptions, 'method' | 'flow'>;

export interface BindCardOptionsV2 extends BindOptionsBaseV2 {
    currency: `${Currency}`;
}

export interface BindWalletOptionsV2 extends BindOptionsBaseV2 {
    wallet: `${Wallet}`;
}

export interface BindSbpTokenOptionsV2 extends BindOptionsBaseV2 {
    /**
     * On mobile devices bank app links will open using this setting (same window, another one, don't open only send postMessage etc.).
     */
    linkTarget?: LinkTarget;

    /**
     * Bank app would redirect user to this URL after successful binding
     */
    termUrl?: string;
}

export interface BindAppleTokenOptionsV2 extends BindCardOptionsV2 {
    /**
     * Apple Pay merchant validation settings preset id.
     */
    applePayPreset: `${ApplePayPreset}`;

    /**
     * Apple Pay merchant two-letter ISO country code (e.g. "US")
     *
     * See https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/1916117-countrycode
     */
    countryCode: string;

    /**
     * Display name of merchant business entity (e.g. "Yango Play")
     *
     * See "label" in: https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/1916119-total
     */
    businessName: string;

    /**
     * Amount of money to be paid for verification. (e.g. "0.00")
     *
     * Normally this should be "0.00" but for some rare cases when processing doesn't support zero sum verifications
     * we keep an ability to specify non-zero amounts.
     *
     * See "amount" in: https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/1916119-total
     */
    amount: string;

    /**
     * Detailed description of expected payment schedule in Apple Pay standard JSON format.
     *
     * See: https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/3955946-recurringpaymentrequest
     */
    recurringPaymentRequest: ApplePayJS.ApplePayRecurringPaymentRequest;
}

export interface BindGoogleTokenOptionsV2 extends BindCardOptionsV2 {
    /**
     * Google Pay merchant validation settings preset id.
     */
    googlePayPreset: `${GooglePayPreset}`;

    /**
     * Google Pay merchant two-letter ISO country code (e.g. "US")
     *
     * See "countryCode" in: https://developers.google.com/pay/api/web/reference/request-objects#TransactionInfo
     */
    countryCode: string;

    /**
     * Display name of merchant business entity (e.g. "Yango Play")
     *
     * See "merchantName" in: https://developers.google.com/pay/api/web/reference/request-objects#MerchantInfo
     */
    businessName: string;

    /**
     * Amount of money to be paid for verification. (e.g. "0.00")
     *
     * Normally this should be "0.00" but for some rare cases when processing doesn't support zero sum verifications
     * we keep an ability to specify non-zero amounts.
     *
     * See "totalPrice" in: https://developers.google.com/pay/api/web/reference/request-objects#TransactionInfo
     */
    amount: string;

    // TODO: finalize it once we know what exact options are needed
}

export interface BindCardOptions {
    url?: string;
    mode?:
        | 'default'
        | 'redirect'
        | 'redirect_to_trust_url_always'
        | 'show_dialog_and_redirect'
        | 'show_dialog_and_redirect_to_trust_url'
        | 'spin'
        | 'redirect_always';
    modeSuccess?:
        | 'default'
        | 'redirect'
        | 'redirect_to_trust_url_always'
        | 'show_dialog_and_redirect'
        | 'show_dialog_and_redirect_to_trust_url'
        | 'spin'
        | 'redirect_always';
    lang?: `${Language}`;
    isMobile?: boolean;
    theme?: 'dark' | 'default' | 'auto'; // Kept as is since it's not compatible with shared Theme
    serviceToken?: string;
    template?: string;
    diehardTemplateTag?: string;
    remoteControl?: boolean;
    queryParams?: Record<string, string>;
    currency?: `${Currency}`;
    polling?: Parameters<Poller>[0] & { enable?: boolean };
}

export interface ApplePayParams {
    apiHost?: ApiHost;
    frameOrigin?: string;

    onSendResponse?(message: FrameApplePayOutgoingMessage): void;
}

export interface BeginApplePaySessionParams {
    apiHost: ApiHost;
    purchaseToken: string;
    merchantId: string;
    request: ApplePayJS.ApplePayPaymentRequest;

    onSendResponse(message: FrameApplePayOutgoingMessage): void;
}

export interface SdkOptions {
    apiHost?: ApiHost;
    frameOrigin?: string;
}
